import classnames from 'classnames'
import * as React from 'react'

import { Button, ButtonHTMLProps, ButtonProps } from '../button'
import { Delayed } from '../delayed'
import { Icon } from '../icon'
import { PropsWithAs } from '../types'
import { useToolbarContext } from './hooks'
import { IToolbarItem, ToolbarTheme } from './interfaces'

function getButtonThemeSettings({
  theme,
  isActive,
  isStretched,
  isInAction,
}: {
  theme: ToolbarTheme
  isStretched: boolean
  isActive?: boolean
  isInAction?: boolean
}): Omit<ButtonHTMLProps, 'as'> & ButtonProps {
  switch (theme) {
    case ToolbarTheme.alternative:
      return {
        className: classnames('active:bg-white disabled:bg-white disabled:text-gray-300 disabled:hover:text-gray-300', {
          'border border-white text-gray-500 hover:border-gray-100 hover:text-gray-900': !isActive,
          'border border-gray-900 text-gray-900': isActive,
          grow: isStretched,
          'h-10 w-10': !isStretched,
          'animate-pulse': isInAction,
        }),
        theme: 'transparent',
        rounding: 'none',
      }
    default:
      return {
        className: classnames(
          'dark:disabled:bg-gray-800 dark:disabled:text-gray-600 disabled:text-gray-300 disabled:bg-white',
          { 'animate-pulse': isInAction }
        ),
        theme: 'chipTransparent',
        rounding: 'small',
      }
  }
}

export interface ToolbarButtonProps extends Omit<ButtonProps, 'theme'> {
  item: IToolbarItem
  disabledTooltip?: string
  isInAction?: boolean
}

export function ToolbarButton<TagType extends React.ElementType = 'button'>({
  item,
  disabledTooltip,
  isInAction: isInActionProp,
  ...props
}: PropsWithAs<TagType> & ToolbarButtonProps) {
  const { theme, isStretched } = useToolbarContext()
  const isInAction = isInActionProp && item.iconAction !== undefined
  const buttonSettings = getButtonThemeSettings({
    theme,
    isStretched,
    isActive: props.active,
    isInAction: isInAction,
  })

  const getMainIcon = () => (
    <Icon name={(props.active && item.iconActive) || item.icon} width={32} className=" shrink-0" />
  )

  return (
    <Button
      {...buttonSettings}
      {...props}
      appearance={props.appearance ?? 'square'}
      title={props.disabled && disabledTooltip ? disabledTooltip : (props.active && item.titleActive) || item.title}
      size={theme !== ToolbarTheme.alternative ? 'medium' : 'large'}
    >
      {props.children ??
        (isInAction ? (
          <Delayed delay={100} delayComponent={getMainIcon()}>
            <Icon name={item.iconAction} width={32} className="shrink-0" />
          </Delayed>
        ) : (
          getMainIcon()
        ))}
    </Button>
  )
}
